/*
 * PKI.Cms — электронная подпись в формате CMS / PKCS#7 (.sig, .p7s, .p7m, .sgn):
 * разбор, проверка и выпуск.
 *
 * Это формат, в котором подписано почти всё, что человек получает: выписки
 * Росреестра (.xml + .xml.sig), документы из налоговой и суда, договоры от
 * контрагентов, бразильские и европейские .p7s. Внутри — подписанные атрибуты
 * (время подписи, хеш документа), сама подпись и сертификаты. Документ лежит
 * либо внутри («прикреплённая» подпись), либо рядом отдельным файлом
 * («открепленная»).
 *
 * ЧТО ЗНАЧИТ «ПРОВЕРЕНО» — и чего это не значит. Проверка здесь отвечает на
 * вопросы, на которые можно ответить честно, не имея доверенного хранилища:
 *   • документ не изменён после подписания (хеш сходится с подписанным);
 *   • подпись математически сделана ключом из этого сертификата;
 *   • сертификат действовал в момент подписания (по времени из подписи или из
 *     штампа времени);
 *   • цепочка сертификатов внутри конверта подписана сама собой по порядку.
 * Чего здесь НЕТ: отзыв сертификата (CRL/OCSP) и доверие к корневому УЦ. Отчёт
 * говорит об этом явно полями, а не молчанием: «проверку, которая говорит ОК,
 * не проверив половину» cms-read.js справедливо называл хуже отсутствующей.
 *
 * ГОСТ проверяется через PKI.Gost (pki/gost.js), RSA/ECDSA/Ed25519 — через
 * WebCrypto. Неизвестный алгоритм — это «не умеем проверить» (null), а не
 * «подделка» (false): для человека это разные ответы.
 *
 * ES2019, UMD: в браузере — window.PKI.Cms, в Node — module.exports.
 */
(function (root) {
    'use strict';
    var P = (root && root.PKI) || {};
    var req = typeof require !== 'undefined' ? require : null;
    function dep(name, file) { return P[name] || (req ? req(file) : null); }
    var Asn1 = dep('Asn1', './asn1.js');
    var Oid = dep('Oid', './oid.js');
    var Keys = dep('Keys', './keys.js');
    var X509 = dep('X509', './x509.js');
    function Gost() { return (root && root.PKI && root.PKI.Gost) || P.Gost || (req ? req('./gost.js') : null); }
    var TAG = Asn1.TAG;

    var OID = {
        DATA: '1.2.840.113549.1.7.1',
        SIGNED_DATA: '1.2.840.113549.1.7.2',
        CONTENT_TYPE: '1.2.840.113549.1.9.3',
        MESSAGE_DIGEST: '1.2.840.113549.1.9.4',
        SIGNING_TIME: '1.2.840.113549.1.9.5',
        COUNTER_SIGNATURE: '1.2.840.113549.1.9.6',
        SMIME_CAPS: '1.2.840.113549.1.9.15',
        SIGNING_CERT: '1.2.840.113549.1.9.16.2.12',
        SIGNING_CERT_V2: '1.2.840.113549.1.9.16.2.47',
        SIG_TIMESTAMP: '1.2.840.113549.1.9.16.2.14',
        ESC_TIMESTAMP: '1.2.840.113549.1.9.16.2.25',
        CERT_VALUES: '1.2.840.113549.1.9.16.2.23',
        REVOCATION_VALUES: '1.2.840.113549.1.9.16.2.24',
        TSTINFO: '1.2.840.113549.1.9.16.1.4',
        SHA256: '2.16.840.1.101.3.4.2.1',
        ECDSA_SHA256: '1.2.840.10045.4.3.2',
        RSA: '1.2.840.113549.1.1.1',
        RSA_SHA256: '1.2.840.113549.1.1.11',
        RSA_PSS: '1.2.840.113549.1.1.10',
        EC_PUBLIC_KEY: '1.2.840.10045.2.1',
        ED25519: '1.3.101.112'
    };

    var WC_HASH = {
        '1.3.14.3.2.26': 'SHA-1',
        '2.16.840.1.101.3.4.2.1': 'SHA-256',
        '2.16.840.1.101.3.4.2.2': 'SHA-384',
        '2.16.840.1.101.3.4.2.3': 'SHA-512'
    };

    function coded(code, msg) { var e = new Error(msg || code); e.code = code; return e; }
    function subtle() {
        var c = (root && root.crypto) || (typeof crypto !== 'undefined' ? crypto : null);
        if (!c || !c.subtle) throw coded('WEBCRYPTO_UNAVAILABLE');
        return c.subtle;
    }
    function buf(u) { return u.buffer.slice(u.byteOffset, u.byteOffset + u.byteLength); }

    /* ═══ вход: DER, PEM или голый base64 ═══════════════════════════════ */

    var B64_ONLY = /^[A-Za-z0-9+/=\s\r\n]+$/;
    function b64decode(s) {
        var clean = s.replace(/[^A-Za-z0-9+/=]/g, '');
        if (typeof atob === 'function') {
            var bin = atob(clean), out = new Uint8Array(bin.length);
            for (var i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
            return out;
        }
        return new Uint8Array(Buffer.from(clean, 'base64'));
    }

    /**
     * Любое представление подписи → DER. Госуслуги и КриптоАРМ умеют сохранять
     * .sig в base64 (с заголовком -----BEGIN…----- и без), и такой файл не
     * должен выглядеть «не подписью».
     * → { der, encoding: 'der'|'pem'|'base64' } или null.
     */
    function unwrap(bytes) {
        var b = Asn1.u8(bytes);
        if (!b.length) return null;
        if (b[0] === 0x30) return { der: b, encoding: 'der' };
        var head = '';
        var n = Math.min(b.length, 1 << 22);
        for (var i = 0; i < n; i++) head += String.fromCharCode(b[i]);
        if (/^\uFEFF/.test(head) || (b[0] === 0xef && b[1] === 0xbb && b[2] === 0xbf)) head = head.replace(/^(\uFEFF|ï»¿)/, '');
        var pem = /-----BEGIN [A-Z0-9 ]+-----([\s\S]*?)-----END [A-Z0-9 ]+-----/.exec(head);
        if (pem) {
            try { var d = b64decode(pem[1]); if (d[0] === 0x30) return { der: d, encoding: 'pem' }; } catch (e) { /* not base64 */ }
            return null;
        }
        var t = head.trim();
        if (t.length >= 64 && B64_ONLY.test(t)) {
            try { var d2 = b64decode(t); if (d2[0] === 0x30) return { der: d2, encoding: 'base64' }; } catch (e) { /* not base64 */ }
        }
        return null;
    }

    /** Быстрая проверка по первым байтам: похоже ли это на SignedData. */
    function looksLikeCms(head) {
        var b = Asn1.u8(head);
        var OIDB = [0x06, 0x09, 0x2a, 0x86, 0x48, 0x86, 0xf7, 0x0d, 0x01, 0x07, 0x02];
        for (var at = 2; at <= 6; at++) {
            var hit = true;
            for (var i = 0; i < OIDB.length; i++) if (b[at + i] !== OIDB[i]) { hit = false; break; }
            if (hit) return true;
        }
        return false;
    }

    /* ═══ разбор ═══════════════════════════════════════════════════════ */

    function ctx(node, n) {
        if (!node || !node.children) return null;
        for (var i = 0; i < node.children.length; i++) {
            var c = node.children[i];
            if (c.cls === 2 && c.tagNumber === n) return c;
        }
        return null;
    }
    // Содержимое OCTET STRING, в том числе составной (BER, кусками).
    function octets(node) {
        if (!node) return null;
        if (!node.constructed) return node.bytes;
        var parts = [], total = 0;
        (node.children || []).forEach(function (c) { var p = octets(c); if (p) { parts.push(p); total += p.length; } });
        var out = new Uint8Array(total), at = 0;
        parts.forEach(function (p) { out.set(p, at); at += p.length; });
        return out;
    }
    function algOf(node) {
        if (!node || !node.children || !node.children[0]) return { oid: null, params: null };
        return { oid: node.children[0].oid, params: node.children[1] || null };
    }
    function hexOf(bytes) { return Asn1.toHex(bytes).toLowerCase(); }
    function normSerial(bytes) { return Asn1.toHex(bytes).replace(/^(00)+(?=.)/, '').toLowerCase(); }

    function parseAttributes(setNode) {
        var out = [];
        (setNode && setNode.children || []).forEach(function (a) {
            if (!a.children || a.children.length < 2) return;
            out.push({ oid: a.children[0].oid, values: a.children[1].children || [], node: a });
        });
        return out;
    }
    function attr(list, oid) {
        for (var i = 0; i < list.length; i++) if (list[i].oid === oid) return list[i];
        return null;
    }

    /* signingCertificate(V2): хеш сертификата, которым подписант обещал
       подписывать. Без этой привязки подпись можно «переодеть» в другой
       сертификат с тем же ключом (атака подмены, RFC 5035 §1). */
    function parseSigningCert(a, v2) {
        if (!a || !a.values[0]) return null;
        var certs = a.values[0].children && a.values[0].children[0];
        var first = certs && certs.children && certs.children[0];
        if (!first || !first.children) return null;
        var k = first.children, hashOid = v2 ? OID.SHA256 : '1.3.14.3.2.26', idx = 0;
        if (v2 && k[0] && k[0].tag === TAG.SEQUENCE) { hashOid = algOf(k[0]).oid || OID.SHA256; idx = 1; }
        var hashNode = k[idx];
        if (!hashNode || hashNode.tag !== TAG.OCTET_STRING) return null;
        return { hashOid: hashOid, hash: hashNode.bytes };
    }

    function parseSignerInfo(node, depth) {
        var k = node.children || [];
        var i = 0;
        var version = k[i] && k[i].tag === TAG.INTEGER ? parseInt(Asn1.toHex(k[i++].bytes), 16) : 1;
        var sidNode = k[i++];
        var sid;
        if (sidNode && sidNode.cls === 2 && sidNode.tagNumber === 0) {
            sid = { type: 'ski', ski: hexOf(sidNode.bytes) };
        } else {
            sid = {
                type: 'issuerSerial',
                issuerDer: sidNode && sidNode.children && sidNode.children[0] ? sidNode.children[0].full : null,
                serial: sidNode && sidNode.children && sidNode.children[1] ? normSerial(sidNode.children[1].bytes) : ''
            };
        }
        var digestAlg = algOf(k[i++]);
        var signedAttrsNode = null;
        if (k[i] && k[i].cls === 2 && k[i].tagNumber === 0) signedAttrsNode = k[i++];
        var sigAlg = algOf(k[i++]);
        var sigNode = k[i++];
        var unsignedAttrsNode = (k[i] && k[i].cls === 2 && k[i].tagNumber === 1) ? k[i] : null;

        var signedAttrs = signedAttrsNode ? parseAttributes(signedAttrsNode) : [];
        var unsignedAttrs = unsignedAttrsNode ? parseAttributes(unsignedAttrsNode) : [];

        // Хешируется DER подписанных атрибутов с тегом SET (0x31) вместо [0].
        var signedAttrsDer = null;
        if (signedAttrsNode) {
            if (signedAttrsNode.indefinite) {
                signedAttrsDer = Asn1.tlv(0x31, Asn1.concat(signedAttrsNode.children.map(function (c) { return c.full; })));
            } else {
                signedAttrsDer = new Uint8Array(signedAttrsNode.full);
                signedAttrsDer[0] = 0x31;
            }
        }

        var md = attr(signedAttrs, OID.MESSAGE_DIGEST);
        var st = attr(signedAttrs, OID.SIGNING_TIME);
        var ct = attr(signedAttrs, OID.CONTENT_TYPE);
        var signingTime = null;
        if (st && st.values[0]) {
            var tv = st.values[0];
            signingTime = tv.date || null;
            if (!signingTime) { try { signingTime = Asn1.decodeTime(tv.tag, tv.bytes); } catch (e) { signingTime = null; } }
        }

        var out = {
            version: version,
            sid: sid,
            digestAlgorithm: digestAlg.oid,
            signatureAlgorithm: sigAlg.oid,
            signatureParams: sigAlg.params,
            signature: sigNode ? sigNode.bytes : new Uint8Array(0),
            signedAttrs: signedAttrs,
            signedAttrsDer: signedAttrsDer,
            unsignedAttrs: unsignedAttrs,
            contentTypeAttr: ct && ct.values[0] ? ct.values[0].oid : null,
            messageDigest: md && md.values[0] ? md.values[0].bytes : null,
            signingTime: signingTime,
            signingCertV2: parseSigningCert(attr(signedAttrs, OID.SIGNING_CERT_V2), true),
            signingCertV1: parseSigningCert(attr(signedAttrs, OID.SIGNING_CERT), false),
            timestampTokens: [],
            counterSigners: [],
            certIndex: -1
        };
        unsignedAttrs.forEach(function (a) {
            if (a.oid === OID.SIG_TIMESTAMP) {
                a.values.forEach(function (v) { out.timestampTokens.push(v.full); });
            } else if (a.oid === OID.COUNTER_SIGNATURE && (depth || 0) < 3) {
                a.values.forEach(function (v) { try { out.counterSigners.push(parseSignerInfo(v, (depth || 0) + 1)); } catch (e) { /* skip */ } });
            }
        });
        return out;
    }

    /**
     * DER → конверт. Бросает coded('CMS_NOT_SIGNATURE').
     *   { eContentType, content|null, detached, certificates:[{der, cert|null}],
     *     crlCount, signers:[SignerInfo] }
     */
    function parse(der) {
        var rootNode;
        try { rootNode = Asn1.parse(Asn1.u8(der)); }
        catch (e) { throw coded('CMS_NOT_SIGNATURE', 'not DER: ' + e.message); }
        var kids = rootNode.children || [];
        if (!kids.length || kids[0].oid !== OID.SIGNED_DATA) {
            throw coded('CMS_NOT_SIGNATURE', kids[0] && kids[0].oid ? 'content type ' + kids[0].oid : 'no content type');
        }
        var wrap = ctx(rootNode, 0);
        var sd = wrap && wrap.children && wrap.children[0];
        if (!sd || !sd.children) throw coded('CMS_NOT_SIGNATURE', 'empty SignedData');
        var s = sd.children;
        var encap = s[2];
        var eContentType = encap && encap.children && encap.children[0] ? encap.children[0].oid : OID.DATA;
        var eWrap = ctx(encap, 0);
        var content = eWrap && eWrap.children && eWrap.children[0] ? octets(eWrap.children[0]) : null;

        var certificates = [];
        var certsNode = ctx(sd, 0);
        (certsNode && certsNode.children || []).forEach(function (c) {
            if (c.tag !== TAG.SEQUENCE) return;   // attribute certs / other formats
            var entry = { der: c.full, cert: null, error: null };
            try { entry.cert = X509.parse(c.full); } catch (e) { entry.error = e.message; }
            certificates.push(entry);
        });
        var crlsNode = ctx(sd, 1);
        var signerSet = s[s.length - 1];
        var signers = [];
        if (signerSet && signerSet.tag === TAG.SET) {
            (signerSet.children || []).forEach(function (si) { signers.push(parseSignerInfo(si, 0)); });
        }
        var env = {
            eContentType: eContentType,
            content: content && content.length ? content : null,
            detached: !(content && content.length),
            certificates: certificates,
            crlCount: crlsNode && crlsNode.children ? crlsNode.children.length : 0,
            signers: signers,
            der: Asn1.u8(der)
        };
        signers.forEach(function (sg) { sg.certIndex = findCert(sg, certificates); });
        return env;
    }

    function certSki(cert) { return cert && cert.subjectKeyId ? cert.subjectKeyId.replace(/:/g, '').toLowerCase() : null; }
    function findCert(signer, certificates) {
        for (var i = 0; i < certificates.length; i++) {
            var c = certificates[i].cert;
            if (!c) continue;
            if (signer.sid.type === 'ski') { if (certSki(c) === signer.sid.ski) return i; }
            else if (c.serialHex && normSerial(c.serialBytes) === signer.sid.serial &&
                     (!signer.sid.issuerDer || Asn1.bytesEqual(c.issuer.der, signer.sid.issuerDer))) return i;
        }
        return -1;
    }

    /* ═══ хеши ═════════════════════════════════════════════════════════ */

    function hashName(oid) {
        if (WC_HASH[oid]) return WC_HASH[oid];
        var G = Gost(), h = G && G.hashForOid(oid);
        return h ? h.name : (Oid.HASH[oid] || oid);
    }
    /** Инкрементальный хеш по OID: { update(bytes), digest(): Promise<Uint8Array> } или null. */
    function createHash(oid) {
        var G = Gost(), gh = G && G.hashForOid(oid);
        if (gh) {
            var h = gh.create();
            return { update: function (b) { h.update(b); }, digest: function () { return Promise.resolve(h.digest()); } };
        }
        var wc = WC_HASH[oid];
        if (!wc) return null;
        var parts = [];
        return {
            update: function (b) { parts.push(new Uint8Array(b)); },
            digest: function () {
                var total = 0; parts.forEach(function (p) { total += p.length; });
                var all = new Uint8Array(total), at = 0;
                parts.forEach(function (p) { all.set(p, at); at += p.length; });
                return subtle().digest(wc, all).then(function (d) { return new Uint8Array(d); });
            }
        };
    }
    function digest(oid, bytes) {
        var h = createHash(oid);
        if (!h) return Promise.resolve(null);
        h.update(Asn1.u8(bytes));
        return h.digest();
    }

    /* ═══ проверка подписи произвольных байтов ключом сертификата ═══════ */

    function keyKind(cert) {
        var alg = cert && cert.publicKey ? cert.publicKey.algorithmOid : null;
        var G = Gost();
        if (!alg && cert && cert.spki) { try { alg = Asn1.parse(cert.spki).children[0].children[0].oid; } catch (e) { alg = null; } }
        if (G && G.isGostKey(alg)) return 'GOST';
        if (alg === OID.RSA || alg === OID.RSA_PSS) return 'RSA';
        if (alg === OID.EC_PUBLIC_KEY) return 'EC';
        if (alg === OID.ED25519) return 'Ed25519';
        return null;
    }

    /**
     * Подпись `signature` над `data` ключом из сертификата `cert`.
     *   hashOid — алгоритм хеша (digestAlgorithm в CMS);
     *   sigOid/sigParams — для RSA-PSS.
     * → Promise<{ valid: true|false|null, reason? }>
     */
    function verifyBytes(cert, data, signature, hashOid, sigOid, sigParams) {
        var kind = keyKind(cert);
        var G = Gost();
        if (kind === 'GOST') {
            var h = G.hashForOid(hashOid) || G.hashForOid(G.hashOidForKey(cert.publicKey ? cert.publicKey.algorithmOid : null));
            if (!h) return Promise.resolve({ valid: null, reason: 'UNSUPPORTED_HASH:' + hashOid });
            try {
                var key = G.publicKeyFromSpki(cert.spki, Asn1);
                var d = h.create(); d.update(data);
                return Promise.resolve(G.verify(d.digest(), signature, key));
            } catch (e) { return Promise.resolve({ valid: null, reason: 'GOST_ERROR:' + e.message }); }
        }
        var wcHash = WC_HASH[hashOid];
        if (!wcHash && kind !== 'Ed25519') return Promise.resolve({ valid: null, reason: 'UNSUPPORTED_HASH:' + hashOid });
        var sub;
        try { sub = subtle(); } catch (e) { return Promise.resolve({ valid: null, reason: 'WEBCRYPTO_UNAVAILABLE' }); }
        var spki = buf(Asn1.u8(cert.spki));
        if (kind === 'RSA') {
            var pss = sigOid === OID.RSA_PSS;
            var salt = 32;
            if (pss && sigParams && sigParams.children) {
                var saltNode = ctx(sigParams, 2);
                if (saltNode && saltNode.children && saltNode.children[0]) salt = parseInt(Asn1.toHex(saltNode.children[0].bytes), 16);
            }
            var imp = pss ? { name: 'RSA-PSS', hash: wcHash } : { name: 'RSASSA-PKCS1-v1_5', hash: wcHash };
            // Ключ RSA-PSS (OID 1.1.10) WebCrypto не импортирует — берём как rsaEncryption.
            var spkiBytes = cert.publicKey && cert.publicKey.algorithmOid === OID.RSA_PSS ? rsaSpkiFromPss(cert) : spki;
            return sub.importKey('spki', spkiBytes, imp, false, ['verify']).then(function (k) {
                return sub.verify(pss ? { name: 'RSA-PSS', saltLength: salt } : { name: 'RSASSA-PKCS1-v1_5' }, k, buf(signature), buf(data));
            }).then(function (ok) { return { valid: !!ok }; }, function (e) { return { valid: null, reason: 'VERIFY_ERROR:' + e.message }; });
        }
        if (kind === 'EC') {
            var curve = cert.publicKey && cert.publicKey.webcryptoCurve;
            if (!curve) return Promise.resolve({ valid: null, reason: 'UNSUPPORTED_CURVE' });
            var size = { 'P-256': 32, 'P-384': 48, 'P-521': 66 }[curve];
            var raw = X509.ecdsaDerToRaw(signature, size);
            if (!raw) return Promise.resolve({ valid: false, reason: 'BAD_ECDSA_SIG' });
            return sub.importKey('spki', spki, { name: 'ECDSA', namedCurve: curve }, false, ['verify']).then(function (k) {
                return sub.verify({ name: 'ECDSA', hash: wcHash }, k, buf(raw), buf(data));
            }).then(function (ok) { return { valid: !!ok }; }, function (e) { return { valid: null, reason: 'VERIFY_ERROR:' + e.message }; });
        }
        if (kind === 'Ed25519') {
            return sub.importKey('spki', spki, { name: 'Ed25519' }, false, ['verify']).then(function (k) {
                return sub.verify({ name: 'Ed25519' }, k, buf(signature), buf(data));
            }).then(function (ok) { return { valid: !!ok }; }, function () { return { valid: null, reason: 'ED25519_UNAVAILABLE' }; });
        }
        return Promise.resolve({ valid: null, reason: 'UNSUPPORTED_KEY' });
    }
    function rsaSpkiFromPss(cert) {
        var node = Asn1.parse(cert.spki);
        return buf(Asn1.sequence(Asn1.sequence(Asn1.oid(OID.RSA), Asn1.nullValue()), node.children[1].full));
    }

    /** Подпись сертификата ключом издателя — с ГОСТом. */
    function verifyCertSignature(cert, issuer) {
        var G = Gost();
        if (G && G.isGostSignature(cert.signatureAlgorithmOid)) {
            var hashOid = cert.signatureAlgorithmOid === G.OID.SIG_2012_512 ? G.OID.STREEBOG_512
                : cert.signatureAlgorithmOid === G.OID.SIG_2012_256 ? G.OID.STREEBOG_256
                : cert.signatureAlgorithmOid === G.OID.SIG_2001 ? G.OID.GOST94
                : G.hashOidForKey(cert.signatureAlgorithmOid);
            return verifyBytes(issuer, cert.tbsDer, cert.signatureValue, hashOid, cert.signatureAlgorithmOid, null);
        }
        return X509.verifySignature(cert, issuer.spki).then(function (r) { return r; }, function (e) {
            return { valid: null, reason: 'VERIFY_ERROR:' + e.message };
        });
    }

    /* ═══ штамп времени (RFC 3161 внутри CAdES-T) ═══════════════════════ */

    function parseTstInfo(der) {
        var n = Asn1.parse(der), k = n.children || [];
        var mi = k[2];
        var info = {
            policy: k[1] ? k[1].oid : null,
            imprintAlg: mi ? algOf(mi.children[0]).oid : null,
            imprint: mi && mi.children[1] ? mi.children[1].bytes : null,
            serial: k[3] ? normSerial(k[3].bytes) : null,
            genTime: k[4] ? (k[4].date || Asn1.decodeTime(k[4].tag, k[4].bytes)) : null,
            tsaName: null
        };
        var tsa = ctx(n, 0);
        if (tsa && tsa.children && tsa.children[0]) {
            try {
                var gn = X509.parseGeneralNames({ children: [tsa.children[0]] });
                var dn = gn && gn[0] && gn[0].value;
                info.tsaName = typeof dn === 'string' ? dn : (dn && dn.rfc4514) || null;
            } catch (e) { info.tsaName = null; }
        }
        return info;
    }

    /**
     * Проверка штампа времени над подписью `signatureBytes`.
     * → { time, tsa, imprintOk, signatureOk, reason }
     */
    function verifyTimestamp(tokenDer, signatureBytes) {
        var env, info;
        try {
            env = parse(tokenDer);
            if (env.eContentType !== OID.TSTINFO || !env.content) throw coded('NOT_TSTINFO');
            info = parseTstInfo(env.content);
        } catch (e) {
            return Promise.resolve({ time: null, tsa: null, imprintOk: null, signatureOk: null, reason: 'BAD_TOKEN' });
        }
        var tsaCert = env.signers[0] && env.signers[0].certIndex >= 0 ? env.certificates[env.signers[0].certIndex].cert : null;
        var out = {
            time: info.genTime,
            tsa: info.tsaName || (tsaCert ? displayName(tsaCert.subject) : null),
            tsaCert: tsaCert,
            imprintOk: null, signatureOk: null, reason: null
        };
        return digest(info.imprintAlg, signatureBytes).then(function (d) {
            out.imprintOk = d ? Asn1.bytesEqual(d, info.imprint) : null;
            if (!env.signers[0]) return out;
            return verifySigner(env, env.signers[0], { content: env.content }).then(function (r) {
                out.signatureOk = r.integrity === 'ok' ? r.signature : (r.integrity === 'fail' ? false : null);
                return out;
            });
        });
    }

    /* ═══ проверка подписанта ═══════════════════════════════════════════ */

    function displayName(name) {
        if (!name) return '';
        return name.get('CN') || [name.get('SN'), name.get('GN')].filter(Boolean).join(' ') || name.get('O') || name.rfc4514 || '';
    }

    function certValidAt(cert, when) {
        if (!cert || !when) return null;
        var t = when.getTime();
        if (cert.notBefore && t < cert.notBefore.getTime()) return 'not_yet';
        if (cert.notAfter && t > cert.notAfter.getTime()) return 'expired';
        return 'ok';
    }

    /* Цепочка внутри конверта: издатель — по AKI→SKI, иначе по имени. */
    function buildChain(env, leafIndex) {
        var chain = [], seen = {}, cur = leafIndex;
        while (cur >= 0 && !seen[cur] && chain.length < 8) {
            seen[cur] = 1;
            var c = env.certificates[cur].cert;
            chain.push(cur);
            if (!c || c.selfIssued) break;
            var next = -1;
            for (var i = 0; i < env.certificates.length; i++) {
                var cand = env.certificates[i].cert;
                if (!cand || i === cur) continue;
                var aki = c.authorityKeyId ? c.authorityKeyId.replace(/:/g, '').toLowerCase() : null;
                if (aki && certSki(cand) === aki) { next = i; break; }
                if (!aki && Asn1.bytesEqual(cand.subject.der, c.issuer.der)) { next = i; break; }
            }
            cur = next;
        }
        return chain;
    }

    /**
     * Проверить одного подписанта.
     *   opts.content      — байты документа (прикреплённого или приложенного);
     *   opts.contentDigest(oid) → Promise<Uint8Array|null> — для больших файлов
     *                        (страница считает хеш кусками, с прогрессом).
     * → Promise<report>, где каждое поле — вердикт:
     *   integrity  'ok'|'fail'|'missing'|'unsupported'
     *   signature  true|false|null
     *   certBinding 'ok'|'fail'|'absent'
     *   certTime   'ok'|'expired'|'not_yet'|null
     */
    function verifySigner(env, signer, opts) {
        opts = opts || {};
        var certEntry = signer.certIndex >= 0 ? env.certificates[signer.certIndex] : null;
        var cert = certEntry ? certEntry.cert : null;
        var report = {
            signer: signer,
            cert: cert,
            certDer: certEntry ? certEntry.der : null,
            digestName: hashName(signer.digestAlgorithm),
            integrity: 'missing',
            signature: null,
            signatureReason: null,
            certBinding: 'absent',
            certTime: null,
            signedAt: signer.signingTime,
            timestamp: null,
            chain: []
        };
        // 1. Хеш документа.
        var contentDigestP;
        if (opts.contentDigest) contentDigestP = Promise.resolve(opts.contentDigest(signer.digestAlgorithm));
        else if (opts.content) contentDigestP = digest(signer.digestAlgorithm, opts.content);
        else contentDigestP = Promise.resolve(undefined);

        return contentDigestP.then(function (cd) {
            if (cd === undefined) report.integrity = 'missing';
            else if (cd === null) report.integrity = 'unsupported';
            else if (signer.signedAttrsDer) {
                report.integrity = signer.messageDigest && Asn1.bytesEqual(cd, signer.messageDigest) ? 'ok' : 'fail';
            } else {
                report.integrity = 'deferred';   // без атрибутов подпись идёт прямо по документу
            }
            // 2. Сама подпись.
            if (!cert) { report.signatureReason = 'NO_CERT'; return null; }
            if (signer.signedAttrsDer) {
                return verifyBytes(cert, signer.signedAttrsDer, signer.signature, signer.digestAlgorithm, signer.signatureAlgorithm, signer.signatureParams);
            }
            if (!opts.content) { report.signatureReason = 'NEED_CONTENT'; return null; }
            return verifyBytes(cert, opts.content, signer.signature, signer.digestAlgorithm, signer.signatureAlgorithm, signer.signatureParams);
        }).then(function (sig) {
            if (sig) { report.signature = sig.valid; report.signatureReason = sig.reason || null; }
            if (report.integrity === 'deferred') report.integrity = report.signature === true ? 'ok' : (report.signature === false ? 'fail' : 'missing');
            // 3. Привязка сертификата (signingCertificateV2).
            var bind = signer.signingCertV2 || signer.signingCertV1;
            if (bind && report.certDer) {
                return digest(bind.hashOid, report.certDer).then(function (h) {
                    report.certBinding = h ? (Asn1.bytesEqual(h, bind.hash) ? 'ok' : 'fail') : 'absent';
                });
            }
            return null;
        }).then(function () {
            // 4. Штамп времени над подписью.
            if (!signer.timestampTokens.length) return null;
            return verifyTimestamp(signer.timestampTokens[0], signer.signature).then(function (ts) { report.timestamp = ts; });
        }).then(function () {
            var when = report.timestamp && report.timestamp.imprintOk && report.timestamp.time ? report.timestamp.time : signer.signingTime;
            report.certTime = certValidAt(cert, when);
            report.timeSource = report.timestamp && report.timestamp.imprintOk ? 'timestamp' : (signer.signingTime ? 'claimed' : null);
            // 5. Цепочка внутри конверта.
            if (signer.certIndex < 0) return report;
            var idx = buildChain(env, signer.certIndex);
            var steps = idx.map(function (ci, n) {
                var c = env.certificates[ci].cert;
                var issuerIdx = idx[n + 1];
                var issuer = issuerIdx !== undefined ? env.certificates[issuerIdx].cert : (c && c.selfIssued ? c : null);
                return { cert: c, issuer: issuer, selfSigned: !!(c && c.selfIssued) };
            });
            return Promise.all(steps.map(function (st) {
                if (!st.cert || !st.issuer) return Promise.resolve({ valid: null, reason: 'NO_ISSUER' });
                return verifyCertSignature(st.cert, st.issuer);
            })).then(function (res) {
                report.chain = steps.map(function (st, n) {
                    return { cert: st.cert, selfSigned: st.selfSigned, issuerFound: !!st.issuer, signature: res[n].valid, reason: res[n].reason || null };
                });
                return report;
            });
        });
    }

    /** Проверить всех подписантов (и заверяющие подписи). */
    function verify(env, opts) {
        return Promise.all(env.signers.map(function (s) {
            return verifySigner(env, s, opts).then(function (r) {
                if (!s.counterSigners.length) return r;
                // Заверяющая подпись подписывает ЗНАЧЕНИЕ подписи основного подписанта.
                return Promise.all(s.counterSigners.map(function (cs) {
                    return verifySigner(env, cs, { content: s.signature });
                })).then(function (cr) { r.counterSignatures = cr; return r; });
            });
        }));
    }

    /* ═══ выпуск подписи (CAdES-BES) ════════════════════════════════════ */

    /**
     * Собрать CMS SignedData над документом.
     *   opts.certDer     — сертификат подписанта (DER);
     *   opts.privateKey  — CryptoKey (ECDSA P-256/P-384 или RSASSA-PKCS1-v1_5);
     *   opts.contentDigest — SHA-256 документа (Uint8Array, 32 байта);
     *   opts.content     — байты документа, если подпись прикреплённая;
     *   opts.signingTime — Date (по умолчанию сейчас);
     *   opts.extraCerts  — [DER] (цепочка);
     *   opts.timestamp   — function(signatureBytes) → Promise<DER токена RFC 3161 | null>:
     *                      штамп доверенного времени над значением подписи (CAdES-T).
     * → Promise<{ der, signature, signerInfoDer }>
     * Подписанные атрибуты: contentType, signingTime, messageDigest,
     * signingCertificateV2 — как у CAdES-BES; подпись проверяется openssl cms.
     */
    function build(opts) {
        var cert = X509.parse(opts.certDer);
        var kind = keyKind(cert);
        var when = opts.signingTime || new Date();
        var sub = subtle();
        return sub.digest('SHA-256', buf(Asn1.u8(opts.certDer))).then(function (certHash) {
            var attrs = [
                Asn1.sequence(Asn1.oid(OID.CONTENT_TYPE), Asn1.set(Asn1.oid(OID.DATA))),
                Asn1.sequence(Asn1.oid(OID.SIGNING_TIME), Asn1.set(Asn1.time(when))),
                Asn1.sequence(Asn1.oid(OID.MESSAGE_DIGEST), Asn1.set(Asn1.octetString(Asn1.u8(opts.contentDigest)))),
                Asn1.sequence(Asn1.oid(OID.SIGNING_CERT_V2), Asn1.set(
                    Asn1.sequence(Asn1.sequence(Asn1.sequence(
                        Asn1.octetString(new Uint8Array(certHash)),
                        Asn1.sequence(Asn1.sequence(Asn1.explicit(4, cert.issuer.der)), Asn1.integer(cert.serialBytes))
                    )))
                ))
            ];
            var signedSet = Asn1.setOf(attrs);          // DER: элементы отсортированы
            var sigAlg, signP;
            if (kind === 'EC') {
                // ecdsa-with-SHA256 на любой кривой: хеш подписанных атрибутов
                // тот же, что у документа (digestAlgorithm = SHA-256).
                sigAlg = Asn1.sequence(Asn1.oid(OID.ECDSA_SHA256));
                signP = sub.sign({ name: 'ECDSA', hash: 'SHA-256' }, opts.privateKey, buf(signedSet)).then(function (raw) {
                    return X509.ecdsaRawToDer(new Uint8Array(raw));
                });
            } else if (kind === 'RSA') {
                sigAlg = Asn1.sequence(Asn1.oid(OID.RSA_SHA256), Asn1.nullValue());
                signP = sub.sign({ name: 'RSASSA-PKCS1-v1_5' }, opts.privateKey, buf(signedSet)).then(function (s) { return new Uint8Array(s); });
            } else {
                return Promise.reject(coded('UNSUPPORTED_SIGNING_KEY'));
            }
            return signP.then(function (signature) {
                // Штамп времени ставится над ЭТИМ значением подписи (CAdES-T):
                // собрать подпись заново и приложить штамп к старой нельзя —
                // ECDSA каждый раз даёт новое значение.
                if (!opts.timestamp) return { signature: signature, unsigned: [] };
                return Promise.resolve(opts.timestamp(signature)).then(function (token) {
                    return { signature: signature, unsigned: token ? [Asn1.sequence(Asn1.oid(OID.SIG_TIMESTAMP), Asn1.set(Asn1.u8(token)))] : [], timestamped: !!token };
                }, function () { return { signature: signature, unsigned: [], timestampFailed: true }; });
            }).then(function (res) {
                var signature = res.signature;
                var unsigned = (opts.unsignedAttrs || []).concat(res.unsigned);
                var signedAttrsImplicit = new Uint8Array(signedSet);
                signedAttrsImplicit[0] = 0xa0;
                var signerInfo = Asn1.sequence(
                    Asn1.integerFromNumber(1),
                    Asn1.sequence(cert.issuer.der, Asn1.integer(cert.serialBytes)),
                    Asn1.sequence(Asn1.oid(OID.SHA256)),
                    signedAttrsImplicit,
                    sigAlg,
                    Asn1.octetString(signature),
                    unsigned.length ? Asn1.context(1, Asn1.concat(unsigned), true) : new Uint8Array(0)
                );
                var encap = opts.content
                    ? Asn1.sequence(Asn1.oid(OID.DATA), Asn1.explicit(0, Asn1.octetString(Asn1.u8(opts.content))))
                    : Asn1.sequence(Asn1.oid(OID.DATA));
                var certs = [Asn1.u8(opts.certDer)].concat((opts.extraCerts || []).map(Asn1.u8));
                var signedData = Asn1.sequence(
                    Asn1.integerFromNumber(1),
                    Asn1.set(Asn1.sequence(Asn1.oid(OID.SHA256))),
                    encap,
                    Asn1.context(0, Asn1.concat(certs), true),
                    Asn1.set(signerInfo)
                );
                var der = Asn1.sequence(Asn1.oid(OID.SIGNED_DATA), Asn1.explicit(0, signedData));
                return { der: der, signature: signature, signerInfoDer: signerInfo, timestamped: !!res.timestamped, timestampFailed: !!res.timestampFailed };
            });
        });
    }

    var API = {
        OID: OID,
        unwrap: unwrap,
        looksLikeCms: looksLikeCms,
        parse: parse,
        parseTstInfo: parseTstInfo,
        verify: verify,
        verifySigner: verifySigner,
        verifyBytes: verifyBytes,
        verifyCertSignature: verifyCertSignature,
        verifyTimestamp: verifyTimestamp,
        createHash: createHash,
        digest: digest,
        hashName: hashName,
        build: build,
        displayName: displayName
    };
    if (typeof module !== 'undefined' && module.exports) module.exports = API;
    if (root) { root.PKI = root.PKI || {}; root.PKI.Cms = API; }
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : null));
